import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useState } from "react";
import { PageShell } from "@/components/PageShell";
import { PromiseBand } from "@/components/PromiseBand";
import { aestheticTreatments, findAestheticTreatment, type AestheticTreatment } from "@/lib/aesthetics-data";
import { absUrl, BRAND, breadcrumbJsonLd } from "@/lib/seo";
import { contact, locations } from "@/lib/healic-data";
import { locationHref } from "@/lib/location-routes";
import {
  ArrowRight,
  Check,
  ChevronDown,
  Clock,
  HeartPulse,
  Phone,
  ShieldCheck,
  Sparkles,
  Timer,
} from "lucide-react";

export const Route = createFileRoute("/aesthetics/$slug")({
  loader: ({ params }): { treatment: AestheticTreatment } => {
    const treatment = findAestheticTreatment(params.slug);
    if (!treatment) throw notFound();
    return { treatment };
  },
  head: ({ loaderData }) => {
    const t = loaderData?.treatment;
    if (!t) return { meta: [{ title: "Aesthetics — Healic Multispeciality Hospital" }] };
    const url = absUrl(`/aesthetics/${t.slug}`);
    const title = `${t.name} — Cost, Sessions & Results | Healic`.slice(0, 70);
    const description = `${t.short} Dermatologist-led ${t.name.toLowerCase()} at Healic Multispeciality Hospital with skin-safe protocols and transparent packages.`.slice(0, 158);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
        { property: "og:title", content: `${t.name} — ${BRAND}` },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "en_IN" },
        { property: "og:site_name", content: BRAND },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: `${t.name} — Healic` },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalProcedure",
            name: t.name,
            description: t.overview,
            howPerformed: t.steps.map((s) => `${s.title}: ${s.points.join(" ")}`).join(" "),
            followup: t.aftercare.join(" "),
            url,
            relevantSpecialty: { "@type": "MedicalSpecialty", name: "Aesthetic Dermatology" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: t.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        breadcrumbJsonLd([
          { name: "Home", url: absUrl("/") },
          { name: "Aesthetics", url: absUrl("/aesthetics") },
          { name: t.name, url },
        ]),
      ],
    };
  },
  notFoundComponent: () => (
    <PageShell>
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Treatment not found</h1>
        <Link to="/aesthetics" className="mt-6 inline-block text-accent">
          Browse aesthetic treatments →
        </Link>
      </div>
    </PageShell>
  ),
  component: AestheticTreatmentPage,
});

function Faq({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
        aria-expanded={open}
      >
        <span className="font-medium">{q}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="px-5 pb-5 text-sm text-muted-foreground">{a}</p>}
    </div>
  );
}

function AestheticTreatmentPage() {
  const { treatment: t } = Route.useLoaderData() as { treatment: AestheticTreatment };
  const related = aestheticTreatments.filter((x) => x.slug !== t.slug).slice(0, 3);

  return (
    <PageShell>
      {/* Hero */}
      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-16">
          <nav aria-label="Breadcrumb" className="text-xs opacity-80">
            <Link to="/" className="hover:text-accent">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/aesthetics" className="hover:text-accent">Aesthetics</Link>
            <span className="mx-2">/</span>
            <span>{t.name}</span>
          </nav>
          <h1 className="mt-4 font-display text-3xl font-bold leading-tight sm:text-4xl">{t.name}</h1>
          <p className="mt-3 max-w-2xl text-sm opacity-90 sm:text-base">{t.tagline}</p>
          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            <div className="rounded-2xl bg-white/10 p-4">
              <Timer className="h-4 w-4 text-accent" />
              <p className="mt-2 text-xs opacity-80">Sessions</p>
              <p className="text-sm font-semibold">{t.sessions}</p>
            </div>
            <div className="rounded-2xl bg-white/10 p-4">
              <Clock className="h-4 w-4 text-accent" />
              <p className="mt-2 text-xs opacity-80">Session time</p>
              <p className="text-sm font-semibold">{t.duration}</p>
            </div>
            <div className="rounded-2xl bg-white/10 p-4">
              <HeartPulse className="h-4 w-4 text-accent" />
              <p className="mt-2 text-xs opacity-80">Downtime</p>
              <p className="text-sm font-semibold">{t.downtime}</p>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/contact" className="rounded-pill bg-accent px-6 py-3 text-sm font-semibold text-accent-foreground">
              Book a consultation
            </Link>
            <a href={`tel:${contact.phoneTel}`} className="inline-flex items-center gap-2 rounded-pill bg-white/10 px-6 py-3 text-sm font-semibold hover:bg-white/15">
              <Phone className="h-4 w-4 text-accent" /> {contact.phone}
            </a>
          </div>
        </div>
      </section>

      <PromiseBand />

      <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-16">
        <div className="grid gap-10 lg:grid-cols-[1.6fr_1fr]">
          <div className="space-y-12">
            {/* Overview */}
            <section>
              <h2 className="font-display text-2xl font-bold">Overview</h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">{t.overview}</p>
            </section>

            {/* Procedure */}
            <section>
              <h2 className="font-display text-2xl font-bold">Procedure</h2>
              <ol className="mt-5 space-y-4">
                {t.steps.map((s, i) => (
                  <li key={s.title} className="rounded-2xl border border-border bg-card p-5">
                    <div className="flex items-center gap-3">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent text-xs font-bold text-accent-foreground">
                        {i + 1}
                      </span>
                      <h3 className="font-display text-base font-semibold">{s.title}</h3>
                    </div>
                    <ul className="mt-3 space-y-2 pl-10 text-sm text-muted-foreground">
                      {s.points.map((p) => (
                        <li key={p} className="list-disc">{p}</li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ol>
            </section>

            {/* Benefits */}
            <section>
              <h2 className="font-display text-2xl font-bold">Benefits</h2>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {t.benefits.map((b) => (
                  <div key={b} className="flex gap-2 rounded-2xl bg-muted/50 p-4 text-sm">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                    <span>{b}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Causes */}
            <section>
              <h2 className="font-display text-2xl font-bold">Causes &amp; who it is for</h2>
              <ul className="mt-5 space-y-2 text-sm text-muted-foreground">
                {t.causes.map((c) => (
                  <li key={c} className="flex gap-2">
                    <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Areas */}
            {t.areas && (
              <section>
                <h2 className="font-display text-2xl font-bold">Areas we treat</h2>
                <div className="mt-5 flex flex-wrap gap-2">
                  {t.areas.map((a) => (
                    <span key={a} className="rounded-pill border border-border bg-card px-4 py-2 text-sm">
                      {a}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {/* Aftercare */}
            <section>
              <h2 className="font-display text-2xl font-bold">Aftercare &amp; results</h2>
              <ul className="mt-5 space-y-2 text-sm text-muted-foreground">
                {t.aftercare.map((a) => (
                  <li key={a} className="flex gap-2">
                    <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* FAQs */}
            <section>
              <h2 className="font-display text-2xl font-bold">Frequently asked questions</h2>
              <div className="mt-5 space-y-3">
                {t.faqs.map((f) => (
                  <Faq key={f.q} q={f.q} a={f.a} />
                ))}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-3xl bg-primary p-6 text-primary-foreground">
              <h2 className="font-display text-lg font-semibold">Talk to our aesthetic team</h2>
              <p className="mt-2 text-sm opacity-90">
                Get a personalised session plan and package price after a skin assessment.
              </p>
              <Link to="/contact" className="mt-4 block rounded-pill bg-accent px-5 py-3 text-center text-sm font-semibold text-accent-foreground">
                Book a consultation
              </Link>
              <a href={`tel:${contact.phoneTel}`} className="mt-3 block rounded-pill bg-white/10 px-5 py-3 text-center text-sm font-semibold hover:bg-white/15">
                Call {contact.phone}
              </a>
            </div>

            <div className="rounded-3xl border border-border bg-card p-6">
              <h2 className="font-display text-lg font-semibold">Available at</h2>
              <ul className="mt-3 space-y-2 text-sm">
                {locations.map((l) => (
                  <li key={l.slug}>
                    <Link to={locationHref(l.slug)} className="text-muted-foreground hover:text-accent">
                      Healic {l.city}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-3xl border border-border bg-card p-6">
              <h2 className="font-display text-lg font-semibold">Other aesthetic treatments</h2>
              <ul className="mt-3 space-y-3 text-sm">
                {related.map((r) => (
                  <li key={r.slug}>
                    <Link
                      to="/aesthetics/$slug"
                      params={{ slug: r.slug }}
                      className="inline-flex items-center gap-1 font-medium text-accent"
                    >
                      {r.name} <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                    <p className="text-xs text-muted-foreground">{r.tagline}</p>
                  </li>
                ))}
              </ul>
              <Link to="/aesthetics" className="mt-4 inline-block text-sm font-semibold text-accent">
                View all treatments →
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </PageShell>
  );
}
